// D288: the ONE rule that decides whether a game rates — the user's, verbatim in spirit:
// "the game is ranked if it starts with at least two humans present; bots may fill up empty
// seats", and "the only reason to unrank a game is if it's all bots".
//
// A HUMAN is any seat that is not one of our bots: signed-in or guest alike. Map, speed, VP
// target, discard limit, a scenario or a pasted map no longer un-rank anything (they did
// through H20 — T-RANK-021/022, Q14). It lives in shared because three places must give the
// same answer — the lobby's live checkmark, the play panel's card, and the server at START —
// and a second copy of a rule is a copy that can disagree (D198).

/** How many distinct humans a table needs, at start, to rate. */
export const RANKED_MIN_HUMANS = 2;

export interface RankVerdict {
  ranked: boolean;
  /** The distinct humans counted (one person in two tabs is one human). */
  humans: number;
  /** Plain words for the ⓘ tooltip — why this table does or does not rate. */
  reason: string;
}

export function rankVerdict(humans: number): RankVerdict {
  const ranked = humans >= RANKED_MIN_HUMANS;
  return { ranked, humans, reason: rankReason(ranked, humans) };
}

function rankReason(ranked: boolean, humans: number): string {
  if (ranked) return `Ranked: ${humans} human players are at the table. Bots may fill the empty seats; only the humans' results move ratings.`;
  if (humans <= 0) return 'Unranked: nobody but bots — a game needs at least two human players to count.';
  return 'Unranked: a game needs at least two human players to count. Bots can fill the other seats.';
}

/**
 * Count distinct humans among seated identities. Each entry is a stable identity key for a
 * human (an account id, else a guest id, else a per-connection key) or null for a bot. The
 * dedupe matters: the same account in two tabs is ONE person, and H6-S4 measured what
 * rating a player against themselves does (a free +162).
 */
export function countHumans(identities: readonly (string | null)[]): number {
  return new Set(identities.filter((k): k is string => k !== null)).size;
}
